import { faTrashCan, faCheck, faChartColumn, faPlay, faMagnifyingGlass, faChevronLeft, faChevronRight, faFilterCircleXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import Processing from "../../Components/Processing/Processing";
import Dropdown from "../../Components/Dropdown/Dropdown";
import DatePicker from "../../Components/DatePicker/DatePicker";
import { SRS_CELLS, LINES, WHOLE_STATION, stateLabelOf } from "../../Functions/htScenarios";
import "../HTTrainingSessions/HTTrainingSessions.css";

const PAGE_SIZE = 10;

// Ongoing / finished HighTension learning sessions the current user takes part
// in. A student sees BOTH their own self-created sessions and the ones their
// teacher assigned; a teacher sees their own table. Fetching, filtering (name /
// cell / creation-date) and paging are all done server-side (single request).
const HTLearningSessions = ({ data, finished } : { data: any, finished: boolean }) => {
    const navigate = useNavigate();
    const [cookies] = useCookies();
    const en = cookies?.language === "en";

    const [sessions, setSessions] = useState<any[] | null>(null);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [page, setPage] = useState(1);

    // Filters. `search` is debounced into `searchQ` so we don't fetch per keypress.
    const [search, setSearch] = useState("");
    const [searchQ, setSearchQ] = useState("");
    const [cell, setCell] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    // Sort: "<field>-<order>" (field = date|name). Default: newest first.
    const [sort, setSort] = useState("date-desc");

    const sortOptions = [
        { value: "date-desc", label: en ? "Date (newest)" : "Data (recente)" },
        { value: "date-asc", label: en ? "Date (oldest)" : "Data (vechi)" },
        { value: "name-asc", label: en ? "Name (A–Z)" : "Nume (A–Z)" },
        { value: "name-desc", label: en ? "Name (Z–A)" : "Nume (Z–A)" },
    ];
    const [sortField, sortOrder] = sort.split("-");

    const myId = data.userData.userId;
    // Path owner = me; extra owner = my teacher (students see assigned sessions too).
    const extraOwners = data.userData?.type === "student" && data.userData.teacherId
        ? [data.userData.teacherId] : [];

    const cellOptions = [
        { id: "", label: en ? "All cells" : "Toate celulele" },
        { id: WHOLE_STATION, label: en ? "Whole / semi station" : "Stație / semistație" },
        ...LINES.map((l) => ({ id: l.id, label: l.label })),
        ...SRS_CELLS.map((c) => ({ id: c.id, label: c.label })),
    ];

    useEffect(() => {
        const t = setTimeout(() => setSearchQ(search.trim()), 350);
        return () => clearTimeout(t);
    }, [search]);

    // Any filter / sort change resets to page 1.
    useEffect(() => { setPage(1); }, [searchQ, cell, dateFrom, dateTo, sort, finished]);

    const load = async () => {
        setSessions(null);
        try {
            const qs = new URLSearchParams({
                owners: extraOwners.join(","),
                finished: String(finished),
                search: searchQ,
                cell,
                dateFrom,
                dateTo,
                sort: sortField,
                order: sortOrder,
                page: String(page),
                limit: String(PAGE_SIZE),
            });
            const request = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/session-htlearning/get-sessions-htlearning/${myId}?${qs.toString()}`, { headers: { "Content-Type": "application/json" } });
            const response = await request.json();
            if (!response.success) { setSessions([]); setTotal(0); setTotalPages(1); return; }
            setSessions(response.sessions || []);
            setTotal(response.total || 0);
            setTotalPages(response.totalPages || 1);
        } catch (e) { setSessions([]); setTotal(0); setTotalPages(1); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [searchQ, cell, dateFrom, dateTo, sort, page, finished]);

    const removeSession = async (ownerId: string, sessionId: string) => {
        if (ownerId !== myId) return;
        try {
            const request = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/session-htlearning/remove-session-htlearning/${myId}/${sessionId}`, { method: "DELETE", headers: { "Content-Type": "application/json" } });
            const response = await request.json();
            if (response.success) await load();
        } catch (e) { /* ignore */ }
    };

    const finishWhole = async (ownerId: string, sessionId: string) => {
        if (ownerId !== myId) return;
        try {
            const request = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/session-htlearning/finish-session-htlearning`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ teacherId: myId, sessionId }),
            });
            const response = await request.json();
            if (response.success) await load();
        } catch (e) { /* ignore */ }
    };

    // "<scheme> <cell> — <initial> → <final>" when the session carries no name.
    const label = (session: any) => {
        if (session.name) return session.name;
        const [ini, fin] = String(session.scenario || "").split(">");
        const target = session.cell === WHOLE_STATION ? (session.semiStation || (en ? "whole station" : "toată stația")) : session.cell;
        const transition = ini && fin ? ` — ${stateLabelOf(ini)} → ${stateLabelOf(fin)}` : "";
        return `${session.schemeName} ${target}${transition}`;
    };
    // createdAt (datetime) -> "DD.MM.YYYY HH:MM".
    const fmtDate = (d: any) => {
        if (!d) return "";
        const dt = new Date(d);
        if (isNaN(dt.getTime())) return "";
        const p = (n: number) => String(n).padStart(2, "0");
        return `${p(dt.getDate())}.${p(dt.getMonth() + 1)}.${dt.getFullYear()} ${p(dt.getHours())}:${p(dt.getMinutes())}`;
    };

    const goReport = (session: any) => navigate(`/session-htlearning/${session.sessionId}/leaderboard`, { state: { sessionName: label(session), ownerId: session.ownerId } });
    const goRun = (session: any) => navigate(`/session-htlearning/${session.sessionId}/0`, { state: { sessionName: label(session), ownerId: session.ownerId } });

    const hasFilters = !!(search || cell || dateFrom || dateTo);
    const clearFilters = () => { setSearch(""); setSearchQ(""); setCell(""); setDateFrom(""); setDateTo(""); };

    return (
        <div className="content ht-sessions">
            {/* Filter bar */}
            <div className="ht-filters">
                <div className="ht-filter search">
                    <FontAwesomeIcon icon={faMagnifyingGlass} className="ht-filter-icon" />
                    <input
                        type="text"
                        placeholder={en ? "Search by name or cell…" : "Caută după nume sau celulă…"}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <div className="ht-filter-cell">
                    <Dropdown
                        value={cell}
                        options={cellOptions.map((o) => ({ value: o.id, label: o.label }))}
                        onChange={setCell}
                        placeholder={en ? "All cells" : "Toate celulele"}
                    />
                </div>
                <DatePicker value={dateFrom} onChange={setDateFrom} en={en} label={en ? "From" : "De la"} placeholder={en ? "From" : "De la"} />
                <DatePicker value={dateTo} onChange={setDateTo} en={en} label={en ? "To" : "Până la"} placeholder={en ? "To" : "Până la"} />
                <div className="ht-filter-sort">
                    <Dropdown value={sort} options={sortOptions} onChange={setSort} label={en ? "Sort by" : "Sorteaza"} />
                </div>
                {hasFilters ? (
                    <button type="button" className="ht-filter-clear" onClick={clearFilters} title={en ? "Clear filters" : "Șterge filtrele"}>
                        <FontAwesomeIcon icon={faFilterCircleXmark} />
                    </button>
                ) : null}
            </div>

            {sessions === null ? (
                <Processing />
            ) : sessions.length ? (
                <>
                    <div className="ht-session-rows">
                        {sessions.map((session: any, idx: number) => {
                            const isOwner = session.ownerId === myId;
                            return (
                                <div className="ht-session-row" key={session.sessionId}>
                                    <div className="ht-session-index">{(page - 1) * PAGE_SIZE + idx + 1}</div>
                                    <div className="ht-session-name">
                                        <span className="ht-session-title">{label(session)}</span>
                                        <span className="ht-session-date">{fmtDate(session.createdAt)}</span>
                                    </div>
                                    <div className="ht-session-actions">
                                        {!finished ? (
                                            <button type="button" className="ht-row-btn play" data-tip={en ? "Open" : "Deschide"} onClick={() => goRun(session)}>
                                                <FontAwesomeIcon icon={faPlay} />
                                            </button>
                                        ) : null}
                                        <button type="button" className="ht-row-btn report" data-tip={en ? "View report" : "Vezi raportul"} onClick={() => goReport(session)}>
                                            <FontAwesomeIcon icon={faChartColumn} />
                                        </button>
                                        {!finished && isOwner ? (
                                            <button type="button" className="ht-row-btn finish" data-tip={en ? "Finish session" : "Încheie sesiunea"} onClick={() => finishWhole(session.ownerId, session.sessionId)}>
                                                <FontAwesomeIcon icon={faCheck} />
                                            </button>
                                        ) : null}
                                        {isOwner ? (
                                            <button type="button" className="ht-row-btn remove" data-tip={en ? "Delete" : "Sterge"} onClick={() => removeSession(session.ownerId, session.sessionId)}>
                                                <FontAwesomeIcon icon={faTrashCan} />
                                            </button>
                                        ) : null}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Pagination */}
                    <div className="ht-pagination">
                        <button type="button" className="ht-page-btn" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                            <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                        <span className="ht-page-info">
                            {en ? "Page" : "Pagina"} {page} / {totalPages} · {total} {en ? "sessions" : "sesiuni"}
                        </span>
                        <button type="button" className="ht-page-btn" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                            <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                    </div>
                </>
            ) : (
                <div className="empty-container">
                    <h2>{hasFilters ? (en ? "No sessions match the filters." : "Nicio sesiune pentru filtrele alese.") : (en ? "No sessions." : "Nicio sesiune.")}</h2>
                </div>
            )}
        </div>
    );
};

export default HTLearningSessions;
